import { type ComponentProps, type ReactNode, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Plus, X } from "lucide-react";
import { FilterTrigger } from "../filter/filter-trigger";
import { useEntitySchemaByName } from "@/hooks/schema/use-entity-schema-by-name";
import { cn } from "@/lib/utils";

// When "All paths" is unchecked we seed the list with the always-present base
// fields so the user starts from a sensible allow-list instead of an empty one.
const DEFAULT_PATHS = ["id", "integer_id", "created_at", "updated_at"];

// `field_paths` is a JSONB column exposed over GraphQL as a String,
// so it round-trips through the form as a JSON-encoded array (same pattern as
// `conditions_on_current`). A missing/invalid value means "all paths" (`["*"]`).
export const parseAllowedPaths = (raw?: string | null): string[] => {
  if (!raw) return ["*"];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((p) => typeof p === "string")) {
      return parsed;
    }
  } catch {
    // fall through to the default below
  }
  return ["*"];
};

export const stringifyAllowedPaths = (paths: string[]): string => JSON.stringify(paths);

// A single `["*"]` entry is the wildcard. An empty array is "nothing allowed"
// and is intentionally NOT treated as the wildcard, so clearing every item does
// not flip the checkbox back on.
const isAllPaths = (paths: string[]) => paths.length === 1 && paths[0] === "*";

const stripNegation = (path: string) => (path.startsWith("!") ? path.slice(1) : path);

interface AllowedPathsEditorProps {
  /**
   * Entity the permission's ACO targets — the root of the path picker. Relation
   * paths only make sense for an entity-backed ACO, so when this is empty the
   * editor renders nothing.
   */
  entityName?: string;
  paths: string[];
  onChange: (paths: string[]) => void;
  disabled?: boolean;
}

export const AllowedPathsEditor = ({
  entityName,
  paths,
  onChange,
  disabled,
}: AllowedPathsEditorProps) => {
  const allPaths = isAllPaths(paths);
  const { entitySchema } = useEntitySchemaByName(entityName ?? "");

  // Without an entity-backed ACO there are no relation paths to pick.
  if (!entityName) return null;

  const handleToggleAll = (checked: boolean) => {
    onChange(checked ? ["*"] : [...DEFAULT_PATHS]);
  };

  const addPath = (path: string) => {
    if (!path) return;
    const stripped = stripNegation(path);
    // Don't add a path that's already present in either polarity. `*` and `*.*`
    // are distinct patterns (wildcard vs. one-level-deep), so they don't collide.
    if (paths.some((p) => stripNegation(p) === stripped)) return;
    onChange([...paths, path]);
  };

  const removePath = (index: number) => {
    onChange(paths.filter((_, i) => i !== index));
  };

  const toggleNegate = (index: number) => {
    onChange(paths.map((p, i) => (i !== index ? p : p.startsWith("!") ? p.slice(1) : `!${p}`)));
  };

  const shared: PathsVariantProps = {
    paths,
    allPaths,
    disabled,
    entitySchema,
    onToggleAll: handleToggleAll,
    onAdd: addPath,
    onRemove: removePath,
    onToggleNegate: toggleNegate,
  };

  return (
    <div>
      <p className="mb-1 text-sm font-medium">Allowed Paths</p>
      <PathsCard {...shared} renderRow={PathRowSegmented} />
    </div>
  );
};

interface PathsVariantProps {
  paths: string[];
  allPaths: boolean;
  disabled?: boolean;
  entitySchema: ComponentProps<typeof FilterTrigger>["entitySchema"];
  onToggleAll: (checked: boolean) => void;
  onAdd: (path: string) => void;
  onRemove: (index: number) => void;
  onToggleNegate: (index: number) => void;
}

const AddPathButton = ({
  entitySchema,
  onAdd,
}: Pick<PathsVariantProps, "entitySchema" | "onAdd">) => (
  <FilterTrigger
    entitySchema={entitySchema}
    onSelect={(field) => onAdd(field.name)}
    asChild
    // No `noPortal`: the popover portals into the dialog container
    // (PopoverContainerContext, provided by PermissionForm), which keeps it
    // interactive and scrollable instead of clipped by the modal.
    popoverSide="bottom"
    popoverAlign="start"
  >
    <Button variant="outline" size="sm" type="button">
      <Plus className="mr-1 h-3.5 w-3.5" />
      Add path
    </Button>
  </FilterTrigger>
);

/** Free-text entry for custom path patterns (e.g. `*.*`) the picker can't express. */
const ManualPathInput = ({ onAdd }: Pick<PathsVariantProps, "onAdd">) => {
  const [value, setValue] = useState("");

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setValue("");
  };

  return (
    <div className="flex items-center gap-2">
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="Custom pattern, e.g. *.*"
        className="h-9 w-48 font-mono text-sm"
      />
      <Button variant="outline" size="sm" type="button" onClick={submit} disabled={!value.trim()}>
        Add
      </Button>
    </div>
  );
};

// TODO(docs): add a "Learn more" link to the relation-path-patterns docs page
// once it exists (slug should cover `*.*` = one level deep, `*` = wildcard,
// `!field` = exclude).
const PathPatternHelp = () => (
  <p className="text-xs text-muted-foreground">
    Use patterns like <code className="font-mono">*.*</code> to allow every path one level deep.
  </p>
);

const EmptyPathsHint = () => (
  <p className="text-xs text-muted-foreground">No paths — nothing on this resource is allowed.</p>
);

interface PathRowProps {
  path: string;
  negated: boolean;
  display: string;
  disabled?: boolean;
  onToggleNegate: () => void;
  onRemove: () => void;
}

const RemovePathButton = ({ disabled, onClick }: { disabled?: boolean; onClick: () => void }) =>
  disabled ? null : (
    <button
      type="button"
      onClick={onClick}
      className="text-muted-foreground hover:text-destructive"
    >
      <X className="h-3.5 w-3.5" />
    </button>
  );

/** Explicit two-segment Include / Exclude switcher per row. */
const PathRowSegmented = ({
  negated,
  display,
  disabled,
  onToggleNegate,
  onRemove,
}: PathRowProps) => (
  <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-2 py-1">
    <div className="inline-flex shrink-0 rounded border bg-background p-0.5 text-xs">
      <button
        type="button"
        disabled={disabled}
        onClick={() => negated && onToggleNegate()}
        className={cn(
          "rounded px-2 py-0.5 font-medium transition-colors",
          !negated ? "bg-emerald-500/15 text-emerald-600" : "text-muted-foreground",
        )}
      >
        Include
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !negated && onToggleNegate()}
        className={cn(
          "rounded px-2 py-0.5 font-medium transition-colors",
          negated ? "bg-destructive/15 text-destructive" : "text-muted-foreground",
        )}
      >
        Exclude
      </button>
    </div>
    <span className="flex-1 truncate font-mono text-sm">{display}</span>
    <RemovePathButton disabled={disabled} onClick={onRemove} />
  </div>
);

/** Two choice cards (All vs Specific) above a path list with a pluggable row. */
const PathsCard = ({
  paths,
  allPaths,
  disabled,
  entitySchema,
  onToggleAll,
  onAdd,
  onRemove,
  onToggleNegate,
  renderRow: Row,
}: PathsVariantProps & { renderRow: (props: PathRowProps) => ReactNode }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-2">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onToggleAll(true)}
        className={cn(
          "rounded-lg border p-3 text-left transition-colors",
          allPaths ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50",
        )}
      >
        <p className="text-sm font-medium text-foreground">All paths</p>
        <p className="text-xs text-muted-foreground">Every relation path on this resource.</p>
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onToggleAll(false)}
        className={cn(
          "rounded-lg border p-3 text-left transition-colors",
          !allPaths ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50",
        )}
      >
        <p className="text-sm font-medium text-foreground">Specific paths</p>
        <p className="text-xs text-muted-foreground">Pick exactly which paths apply.</p>
      </button>
    </div>

    {!allPaths && (
      <div className="space-y-2">
        <div className="space-y-1.5">
          {paths.map((path, idx) => (
            <Row
              key={`${path}-${idx}`}
              path={path}
              negated={path.startsWith("!")}
              display={stripNegation(path)}
              disabled={disabled}
              onToggleNegate={() => onToggleNegate(idx)}
              onRemove={() => onRemove(idx)}
            />
          ))}
          {paths.length === 0 && <EmptyPathsHint />}
        </div>
        {!disabled && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <AddPathButton entitySchema={entitySchema} onAdd={onAdd} />
              <ManualPathInput onAdd={onAdd} />
            </div>
            <PathPatternHelp />
          </div>
        )}
      </div>
    )}
  </div>
);
